/**
 * Cursor-flicker diagnostic.
 *
 * The bug under investigation: the OS/browser-resolved cursor (pointer,
 * default, text/I-beam, ...) rapidly alternates between two states while the
 * mouse is essentially stationary, over completely unrelated surfaces (the
 * Dock, Space Chat, and plain text inside the Docs editor). That breadth
 * rules out any single component's CSS — it points at something global:
 * either the element `document.elementFromPoint()` resolves at the cursor's
 * position is itself changing every few frames (an overlay/portal
 * mounting/unmounting, moving, or toggling `pointer-events`), or a class/
 * inline-style mutation on an ancestor is flipping the *resolved* `cursor`
 * value on the same, unmoving element.
 *
 * This hooks a requestAnimationFrame loop to sample the resolved cursor at
 * the last known mouse position every frame — independent of any single
 * component's hover state — and flags bursts of change while the mouse
 * hasn't meaningfully moved. A MutationObserver on the whole document runs
 * in parallel so a flagged burst can be correlated with whatever DOM
 * mutation caused it.
 *
 * Enable with `?debug_cursor_flicker=1` or
 * `localStorage.setItem('debug_cursor_flicker', '1')`, then hover the
 * flickering area and hold the mouse still — watch the console for
 * `[CURSOR_FLICKER]` groups. Also exposed as `window.__cursorFlicker` for
 * manual inspection (`.getLog()`, `.getMutations()`, `.stop()`).
 */

const STATIONARY_PX = 4; // max cursor movement to still count as "resting"
const STATIONARY_WINDOW_MS = 250; // look-back window for the "resting" check
const FLICKER_MIN_TRANSITIONS = 3; // transitions within FLICKER_WINDOW_MS while resting
const FLICKER_WINDOW_MS = 600;
const MAX_LOG_ENTRIES = 200;
const MAX_MUTATION_ENTRIES = 200;

interface TransitionEntry {
  t: number;
  x: number;
  y: number;
  prevCursor: string;
  nextCursor: string;
  prevEl: string;
  nextEl: string;
  chain: string[];
}

interface MutationEntry {
  t: number;
  type: string;
  target: string;
  attr?: string;
}

let started = false;
let rafHandle: number | null = null;
let mouseX = -1;
let mouseY = -1;
let posHistory: { t: number; x: number; y: number }[] = [];
let lastCursor = '';
let lastElDesc = '';
let log: TransitionEntry[] = [];
let mutationLog: MutationEntry[] = [];
let mutationObserver: MutationObserver | null = null;
let flickerGroupOpen = false;
let lastFlickerLoggedAt = 0;

function isCursorFlickerDebug(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    localStorage.getItem('debug_cursor_flicker') === '1' ||
    new URLSearchParams(window.location.search).get('debug_cursor_flicker') === '1'
  );
}

function describeElement(el: Element | null): string {
  if (!el) return '(none)';
  const tag = el.tagName.toLowerCase();
  const id = el.id ? `#${el.id}` : '';
  const cls = el.classList && el.classList.length ? `.${Array.from(el.classList).slice(0, 4).join('.')}` : '';
  return `${tag}${id}${cls}`;
}

/**
 * Walk up from `el` and describe every ancestor that establishes its own
 * stacking context (non-static position + z-index, or a portal boundary),
 * since that's exactly the information needed to explain "why did hit-testing
 * pick THIS element instead of the one visually underneath."
 */
function describeStackingChain(el: Element | null): string[] {
  const chain: string[] = [];
  let node: Element | null = el;
  let depth = 0;
  while (node && depth < 12) {
    const cs = window.getComputedStyle(node);
    const z = cs.zIndex;
    const pos = cs.position;
    const pe = cs.pointerEvents;
    const interesting = (z !== 'auto' && pos !== 'static') || pe === 'none' || node.id === 'portal' || node.id === 'root';
    if (interesting) {
      chain.push(`${describeElement(node)} pos=${pos} z=${z} pointer-events=${pe}`);
    }
    node = node.parentElement;
    depth++;
  }
  return chain;
}

function recordTransition(entry: TransitionEntry): void {
  log.push(entry);
  if (log.length > MAX_LOG_ENTRIES) log.shift();

  const windowStart = entry.t - FLICKER_WINDOW_MS;
  const recentCount = log.filter((e) => e.t >= windowStart).length;

  // Only alarm while the cursor has been resting — ordinary mouse travel
  // across many elements produces lots of legitimate transitions and isn't
  // what we're hunting for.
  const resting = isResting(entry.t);

  if (resting && recentCount >= FLICKER_MIN_TRANSITIONS) {
    if (!flickerGroupOpen || entry.t - lastFlickerLoggedAt > 1500) {
      flickerGroupOpen = true;
      // eslint-disable-next-line no-console
      console.warn(
        `%c[CURSOR_FLICKER] FLICKER DETECTED — ${recentCount} cursor/element changes in ${FLICKER_WINDOW_MS}ms while the mouse rested at (${entry.x}, ${entry.y})`,
        'color: #fff; background: #d33; padding: 2px 6px; border-radius: 3px; font-weight: bold;',
      );
    }
    lastFlickerLoggedAt = entry.t;
    // eslint-disable-next-line no-console
    console.warn(
      `[CURSOR_FLICKER]   ${entry.prevCursor} → ${entry.nextCursor}  |  ${entry.prevEl} → ${entry.nextEl}`,
    );
    if (entry.chain.length) {
      // eslint-disable-next-line no-console
      console.warn('[CURSOR_FLICKER]   stacking chain at hit point:', entry.chain);
    }
    const recentMutations = mutationLog.filter((m) => Math.abs(m.t - entry.t) < 100);
    if (recentMutations.length) {
      // eslint-disable-next-line no-console
      console.warn('[CURSOR_FLICKER]   DOM mutations within 100ms of this transition:', recentMutations);
    }
  }
}

function isResting(now: number): boolean {
  const windowStart = now - STATIONARY_WINDOW_MS;
  const recent = posHistory.filter((p) => p.t >= windowStart);
  if (recent.length < 2) return true;
  let maxDx = 0;
  let maxDy = 0;
  const first = recent[0];
  for (const p of recent) {
    maxDx = Math.max(maxDx, Math.abs(p.x - first.x));
    maxDy = Math.max(maxDy, Math.abs(p.y - first.y));
  }
  return maxDx <= STATIONARY_PX && maxDy <= STATIONARY_PX;
}

function tick(): void {
  if (!started) return;
  const now = performance.now();
  if (mouseX >= 0 && mouseY >= 0) {
    const el = document.elementFromPoint(mouseX, mouseY);
    const cursor = el ? window.getComputedStyle(el).cursor : 'none';
    const elDesc = describeElement(el);
    if (cursor !== lastCursor || elDesc !== lastElDesc) {
      if (lastElDesc) {
        recordTransition({
          t: now,
          x: mouseX,
          y: mouseY,
          prevCursor: lastCursor,
          nextCursor: cursor,
          prevEl: lastElDesc,
          nextEl: elDesc,
          chain: describeStackingChain(el),
        });
      }
      lastCursor = cursor;
      lastElDesc = elDesc;
    }
  }
  rafHandle = requestAnimationFrame(tick);
}

function handleMouseMove(e: MouseEvent): void {
  mouseX = e.clientX;
  mouseY = e.clientY;
  const now = performance.now();
  posHistory.push({ t: now, x: mouseX, y: mouseY });
  const cutoff = now - STATIONARY_WINDOW_MS * 4;
  while (posHistory.length && posHistory[0].t < cutoff) posHistory.shift();
}

function handleMutation(mutations: MutationRecord[]): void {
  const now = performance.now();
  for (const m of mutations) {
    mutationLog.push({
      t: now,
      type: m.type,
      target: describeElement(m.target as Element),
      attr: m.attributeName ?? undefined,
    });
  }
  if (mutationLog.length > MAX_MUTATION_ENTRIES) {
    mutationLog = mutationLog.slice(-MAX_MUTATION_ENTRIES);
  }
}

export function startCursorFlickerDebug(): void {
  if (started || typeof window === 'undefined' || !isCursorFlickerDebug()) return;
  started = true;

  window.addEventListener('mousemove', handleMouseMove, { passive: true });
  rafHandle = requestAnimationFrame(tick);

  mutationObserver = new MutationObserver(handleMutation);
  mutationObserver.observe(document.body, {
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'style'],
    childList: true,
  });

  (window as any).__cursorFlicker = {
    getLog: () => log.slice(),
    getMutations: () => mutationLog.slice(),
    stop: stopCursorFlickerDebug,
  };

  // eslint-disable-next-line no-console
  console.log(
    '%c[CURSOR_FLICKER] debug active — hold the mouse still over the flickering area and watch for warnings. window.__cursorFlicker.getLog() / .getMutations() for raw data.',
    'color: #0a0; font-weight: bold;',
  );
}

export function stopCursorFlickerDebug(): void {
  started = false;
  if (rafHandle !== null) cancelAnimationFrame(rafHandle);
  rafHandle = null;
  window.removeEventListener('mousemove', handleMouseMove);
  mutationObserver?.disconnect();
  mutationObserver = null;
}
